/**
 * Map Editor Page — dedicated admin route for editing map overlays and photo markers
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as Cesium from 'cesium';
import 'cesium/Build/Cesium/Widgets/widgets.css';
import { api } from '../../services/api';
import { MapEditorProvider, useMapEditor } from './MapEditorProvider';
import { EditorToolbar } from './components/EditorToolbar';
import { LeftPanel } from './components/LeftPanel';
import { RightPropertiesPanel } from './components/RightPropertiesPanel';
import { StatusBar } from './components/StatusBar';
import { createIndividualMarkerSvg, createCategoryMarkerSvg, createAggregateMarkerSvg } from '../../features/photos';
import { ClusterLevel, clusterLevelFromHeight, clusterPhotos } from '../../features/photos';
import { AddFloorPlanModal } from '../../features/floorplans/components/AddFloorPlanModal';
import { FloorPlanAligner } from '../../features/floorplans/components/FloorPlanAligner';
import { calculateBoundsFromTransform } from '../../../shared/types/floorplan';
import type { FloorPlanOverlay, FloorPlanTransform } from '../../../shared/types/floorplan';
import { DEFAULT_PHOTO_CATEGORIES } from '../../../shared/types/project';
import type { Project, PhotoData, PhotoCategory } from '../../../shared/types/project';
import { createBoundedImageryProvider } from '../../utils/boundedImageryProvider';
import { patchCesiumSkeletons } from '../../utils/patchCesiumSkeletons';

// Apply Cesium bug fix before any Viewer is created
patchCesiumSkeletons();

// ─── Desktop gate ──────────────────────────────────────────────────────────

function DesktopGate({ children }: { children: React.ReactNode }) {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  if (!isDesktop) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0d0d0d', color: '#f1f5f9', padding: 40, textAlign: 'center' }}>
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
        </svg>
        <h2 style={{ fontSize: 20, fontWeight: 600, marginTop: 20, marginBottom: 8 }}>Desktop Required</h2>
        <p style={{ color: '#64748b', fontSize: 14, maxWidth: 360, lineHeight: 1.6 }}>
          The Map Editor requires a desktop browser for precise overlay and marker editing.
        </p>
        <button onClick={() => navigate(-1)} style={{ marginTop: 20, padding: '10px 24px', background: '#4F7CFF', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
          Go Back
        </button>
      </div>
    );
  }

  return <>{children}</>;
}

// ─── Main editor content ───────────────────────────────────────────────────

function MapEditorContent({ project }: { project: Project }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<Cesium.Viewer | null>(null);
  const photoEntitiesRef = useRef<Map<string, Cesium.Entity>>(new Map());
  const orthoLayerRef = useRef<Cesium.ImageryLayer | null>(null);
  const dsmLayerRef = useRef<Cesium.ImageryLayer | null>(null);
  const floorPlanLayersRef = useRef<Map<string, Cesium.ImageryLayer>>(new Map());
  const draggingRef = useRef<{ photoId: string; entity: Cesium.Entity } | null>(null);
  const [showAddFloorPlan, setShowAddFloorPlan] = useState(false);
  const { state, dispatch, pushAction } = useMapEditor();
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const projectRef = useRef(project);
  projectRef.current = project;

  // Initialize data from project
  useEffect(() => {
    const geotiff = project.geotiff;
    dispatch({ type: 'SET_PHOTOS', photos: geotiff?.photos || [] });
    dispatch({ type: 'SET_FLOOR_PLANS', floorPlans: geotiff?.floorPlans?.overlays || [] });
    dispatch({ type: 'SET_CUSTOM_CATEGORIES', categories: geotiff?.customCategories || [] });
  }, [project.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save when dirty
  useEffect(() => {
    if (!state.isDirty) return;
    dispatch({ type: 'SET_SAVE_STATUS', status: 'unsaved' });

    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      dispatch({ type: 'SET_SAVE_STATUS', status: 'saving' });
      try {
        const geotiff = projectRef.current.geotiff || {};
        await api.project.update({
          id: projectRef.current.id,
          updates: {
            geotiff: {
              ...geotiff,
              photos: state.photos,
              floorPlans: { ...geotiff.floorPlans, overlays: state.floorPlans },
              customCategories: state.customCategories,
            },
          },
        });
        dispatch({ type: 'SET_DIRTY', dirty: false });
        dispatch({ type: 'SET_SAVE_STATUS', status: 'saved' });
      } catch {
        dispatch({ type: 'SET_SAVE_STATUS', status: 'error' });
      }
    }, 3000);

    return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); };
  }, [state.isDirty, state.photos, state.floorPlans, state.customCategories]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize Cesium
  useEffect(() => {
    if (!containerRef.current || viewerRef.current) return;

    // Disable Cesium Ion — we use Mapbox for basemaps
    Cesium.Ion.defaultAccessToken = undefined as unknown as string;

    // Create viewer with no default imagery (baseLayer: false prevents Ion fetch)
    const viewer = new Cesium.Viewer(containerRef.current, {
      baseLayerPicker: false,
      geocoder: false,
      homeButton: false,
      infoBox: false,
      navigationHelpButton: false,
      // @ts-expect-error sceneModeLauncher exists at runtime
      sceneModeLauncher: false,
      selectionIndicator: false,
      timeline: false,
      animation: false,
      fullscreenButton: false,
      vrButton: false,
      sceneMode: Cesium.SceneMode.SCENE2D,
      mapProjection: new Cesium.WebMercatorProjection(),
      baseLayer: false,
      terrainProvider: new Cesium.EllipsoidTerrainProvider(),
      requestRenderMode: true,
      maximumRenderTimeChange: Infinity,
    });
    viewer.creditDisplay.container.style.display = 'none';
    viewer.screenSpaceEventHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
    viewer.scene.globe.depthTestAgainstTerrain = false;
    viewer.scene.backgroundColor = Cesium.Color.fromCssColorString('#1a1a1a');

    viewerRef.current = viewer;

    // Add satellite basemap
    const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN;
    if (mapboxToken) {
      const provider = new Cesium.UrlTemplateImageryProvider({
        url: `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/tiles/512/{z}/{x}/{y}@2x?access_token=${mapboxToken}`,
        maximumLevel: 22,
        tileWidth: 512,
        tileHeight: 512,
      });
      viewer.imageryLayers.addImageryProvider(provider, 0);
    }

    // Load ortho tiles if available
    const geotiff = project.geotiff;
    const orthoCache = geotiff?.orthoTileCache;
    const orthoMeta = geotiff?.orthoTileMetadata;
    if (orthoCache && orthoMeta?.boundsWgs84) {
      const bounds = orthoMeta.boundsWgs84;
      const offset = geotiff?.positionOffset || { lng: 0, lat: 0 };
      const rectangle = Cesium.Rectangle.fromDegrees(
        bounds.minX + offset.lng, bounds.minY + offset.lat,
        bounds.maxX + offset.lng, bounds.maxY + offset.lat
      );
      try {
        let orthoProvider: Cesium.UrlTemplateImageryProvider;

        if (orthoMeta.isGlobalScheme && orthoMeta.minZoom !== undefined && orthoMeta.maxZoom !== undefined) {
          // Global Web Mercator tiles — bounded provider returns transparent
          // blanks for z < minZoom, preventing 404s and cache overflow.
          orthoProvider = createBoundedImageryProvider(
            `/api/tiles/${orthoCache}/{z}/{x}/{y}.${orthoMeta.format || 'png'}`,
            orthoMeta.minZoom,
            orthoMeta.maxZoom,
            rectangle,
            orthoMeta.tileSize || 256,
          );
        } else {
          // Local pyramid tiles
          orthoProvider = new Cesium.UrlTemplateImageryProvider({
            url: `/api/tiles/${orthoCache}/{z}/{x}/{y}.${orthoMeta.format || 'png'}?direct=true`,
            tilingScheme: new Cesium.GeographicTilingScheme({
              rectangle,
              numberOfLevelZeroTilesX: 1,
              numberOfLevelZeroTilesY: 1,
            }),
            minimumLevel: 0,
            maximumLevel: (orthoMeta.levels || 20) - 1,
            tileWidth: orthoMeta.tileSize || 256,
            tileHeight: orthoMeta.tileSize || 256,
          });
        }

        // Suppress expected 404s for tiles outside data coverage
        orthoProvider.errorEvent.addEventListener((err: Cesium.TileProviderError) => {
          err.retry = false;
        });

        // Rectangle constraint is on the provider (not the layer) to prevent
        // global enumeration without triggering Cesium 1.137 skeleton crash.
        const orthoLayer = new Cesium.ImageryLayer(orthoProvider);
        viewer.imageryLayers.add(orthoLayer);
        orthoLayerRef.current = orthoLayer;
      } catch (e) {
        console.error('[MapEditor] Failed to load ortho tiles:', e);
      }
    }

    // Fly to project location
    const tileBounds = orthoMeta?.boundsWgs84 || geotiff?.dsmTileMetadata?.boundsWgs84;
    if (tileBounds) {
      const offset = geotiff?.positionOffset || { lng: 0, lat: 0 };
      requestAnimationFrame(() => {
        if (!viewer.isDestroyed()) {
          viewer.camera.setView({
            destination: Cesium.Rectangle.fromDegrees(
              tileBounds.minX + offset.lng, tileBounds.minY + offset.lat,
              tileBounds.maxX + offset.lng, tileBounds.maxY + offset.lat
            ),
          });
        }
      });
    } else if (project.latitude != null && project.longitude != null) {
      requestAnimationFrame(() => {
        if (!viewer.isDestroyed()) {
          viewer.camera.flyTo({
            destination: Cesium.Cartesian3.fromDegrees(project.longitude!, project.latitude!, 500),
            orientation: { heading: 0, pitch: Cesium.Math.toRadians(-60), roll: 0 },
            duration: 0,
          });
        }
      });
    } else if (project.address || project.city) {
      const query = [project.address, project.city, project.state].filter(Boolean).join(', ');
      if (query && mapboxToken) {
        fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${mapboxToken}&limit=1`)
          .then(r => r.json())
          .then(data => {
            const f = data.features?.[0];
            if (f && !viewer.isDestroyed()) {
              viewer.camera.flyTo({
                destination: Cesium.Cartesian3.fromDegrees(f.center[0], f.center[1], 500),
                orientation: { heading: 0, pitch: Cesium.Math.toRadians(-60), roll: 0 },
                duration: 0,
              });
            }
          })
          .catch(() => {});
      }
    }

    // Track cursor position
    const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    handler.setInputAction((movement: Cesium.ScreenSpaceEventHandler.MotionEvent) => {
      const cartesian = viewer.camera.pickEllipsoid(movement.endPosition, viewer.scene.globe.ellipsoid);
      if (cartesian) {
        const carto = Cesium.Cartographic.fromCartesian(cartesian);
        dispatch({ type: 'SET_CURSOR', position: {
          lng: Cesium.Math.toDegrees(carto.longitude),
          lat: Cesium.Math.toDegrees(carto.latitude),
        }});
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    return () => {
      handler.destroy();
      viewer.destroy();
      viewerRef.current = null;
    };
  }, [project.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Ortho/DSM visibility sync ─────────────────────────────────────────

  useEffect(() => {
    if (orthoLayerRef.current) orthoLayerRef.current.show = state.orthoVisible;
    if (dsmLayerRef.current) dsmLayerRef.current.show = state.dsmVisible;
    viewerRef.current?.scene.requestRender();
  }, [state.orthoVisible, state.dsmVisible]);

  // ─── Cluster level from camera height ───────────────────────────────────

  const [clusterLevel, setClusterLevel] = useState<ClusterLevel>(ClusterLevel.INDIVIDUAL);
  const clusterLevelRef = useRef<ClusterLevel>(ClusterLevel.INDIVIDUAL);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || viewer.isDestroyed()) return;

    const onCameraChanged = () => {
      const height = viewer.camera.positionCartographic.height;
      const newLevel = clusterLevelFromHeight(height);
      if (newLevel !== clusterLevelRef.current) {
        clusterLevelRef.current = newLevel;
        setClusterLevel(newLevel);
      }
    };

    viewer.camera.changed.addEventListener(onCameraChanged);
    onCameraChanged();

    return () => {
      if (!viewer.isDestroyed()) {
        viewer.camera.changed.removeEventListener(onCameraChanged);
      }
    };
  }, []);

  // ─── Photo marker helpers ──────────────────────────────────────────────

  const allCategories = useMemo(() => [...DEFAULT_PHOTO_CATEGORIES, ...state.customCategories], [state.customCategories]);
  const getCategoryById = useCallback((id: string): PhotoCategory | undefined => allCategories.find(c => c.id === id), [allCategories]);

  // Filter hidden categories then cluster
  const visiblePhotos = useMemo(() => {
    return state.photos.filter(p => !state.categoryFilters.has(p.categoryId));
  }, [state.photos, state.categoryFilters]);

  const photoClusters = useMemo(
    () => clusterPhotos(visiblePhotos, clusterLevel),
    [visiblePhotos, clusterLevel],
  );

  // Render clustered photo markers
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || viewer.isDestroyed()) return;

    // Clear all existing photo entities
    photoEntitiesRef.current.forEach((_entity, key) => {
      const existing = viewer.entities.getById(key);
      if (existing) viewer.entities.remove(existing);
    });
    photoEntitiesRef.current.clear();

    for (const cluster of photoClusters) {
      const count = cluster.photos.length;
      const isSelected = cluster.photos.some(p => state.selectedIds.includes(p.id));
      let svg: string;

      if (cluster.level === ClusterLevel.AGGREGATE) {
        svg = createAggregateMarkerSvg(count, isSelected);
      } else if (cluster.level === ClusterLevel.CATEGORY) {
        const cat = getCategoryById(cluster.categoryId || 'general') || DEFAULT_PHOTO_CATEGORIES[0];
        svg = createCategoryMarkerSvg(cat, count, isSelected);
      } else {
        const cat = getCategoryById(cluster.photos[0].categoryId) || DEFAULT_PHOTO_CATEGORIES[0];
        svg = createIndividualMarkerSvg(cat, count, isSelected);
      }

      const entityId = `cluster-${cluster.id}`;

      // Remove stale
      const stale = viewer.entities.getById(entityId);
      if (stale) viewer.entities.remove(stale);

      const entity = viewer.entities.add({
        id: entityId,
        position: Cesium.Cartesian3.fromDegrees(cluster.center.longitude, cluster.center.latitude, 0),
        billboard: {
          image: svg,
          verticalOrigin: Cesium.VerticalOrigin.CENTER,
          horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          scale: 1.5,
        },
        properties: {
          photoId: cluster.photos[0].id,
          clusterPhotoIds: cluster.photos.map(p => p.id).join(','),
          type: 'photo',
        },
      });

      photoEntitiesRef.current.set(entityId, entity);
    }

    // Temporarily enable continuous rendering for texture upload
    viewer.scene.requestRenderMode = false;
    const timer = setTimeout(() => {
      if (!viewer.isDestroyed()) {
        viewer.scene.requestRenderMode = true;
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [photoClusters, state.selectedIds, getCategoryById]);

  // ─── Photo click + drag interaction ───────────────────────────────────

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || viewer.isDestroyed()) return;

    const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

    // Click handler — place photo or select marker
    handler.setInputAction((click: Cesium.ScreenSpaceEventHandler.PositionedEvent) => {
      // Place photo mode
      if (state.mode === 'place-photo') {
        const cartesian = viewer.camera.pickEllipsoid(click.position, viewer.scene.globe.ellipsoid);
        if (cartesian) {
          const carto = Cesium.Cartographic.fromCartesian(cartesian);
          const lng = Cesium.Math.toDegrees(carto.longitude);
          const lat = Cesium.Math.toDegrees(carto.latitude);

          const newPhoto: PhotoData = {
            id: crypto.randomUUID(),
            name: `Photo ${state.photos.length + 1}`,
            path: '',
            categoryId: 'general',
            coordinates: { longitude: lng, latitude: lat },
            isManuallyPlaced: true,
            addedAt: new Date().toISOString(),
          };

          dispatch({ type: 'ADD_PHOTO', photo: newPhoto });
          dispatch({ type: 'SET_SELECTION', ids: [newPhoto.id], itemType: 'photo' });
          dispatch({ type: 'SET_MODE', mode: 'select' });

          pushAction({
            type: 'ADD_PHOTO',
            description: `Add photo at ${lng.toFixed(4)}, ${lat.toFixed(4)}`,
            undo: () => dispatch({ type: 'DELETE_PHOTOS', ids: [newPhoto.id] }),
            redo: () => dispatch({ type: 'ADD_PHOTO', photo: newPhoto }),
          });
        }
        return;
      }

      // Select mode — pick entity (works for both individual and clustered markers)
      const pickedObject = viewer.scene.pick(click.position);
      if (Cesium.defined(pickedObject) && pickedObject.id) {
        const entity = pickedObject.id as Cesium.Entity;
        const clusterIds = entity.properties?.clusterPhotoIds?.getValue() as string | undefined;
        if (clusterIds) {
          const ids = clusterIds.split(',');
          if (click.position && (window.event as KeyboardEvent)?.shiftKey) {
            ids.forEach(id => dispatch({ type: 'ADD_TO_SELECTION', id }));
          } else {
            dispatch({ type: 'SET_SELECTION', ids, itemType: 'photo' });
          }
          return;
        }
      }

      // Clicked empty space — deselect
      dispatch({ type: 'CLEAR_SELECTION' });
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    // Drag start — pick photo entity (only in move mode, individual markers only)
    handler.setInputAction((click: Cesium.ScreenSpaceEventHandler.PositionedEvent) => {
      if (state.mode !== 'move') return;
      const pickedObject = viewer.scene.pick(click.position);
      if (Cesium.defined(pickedObject) && pickedObject.id) {
        const entity = pickedObject.id as Cesium.Entity;
        const clusterIds = entity.properties?.clusterPhotoIds?.getValue() as string | undefined;
        if (clusterIds) {
          const ids = clusterIds.split(',');
          // Only allow dragging individual markers (single photo), not clusters
          if (ids.length === 1) {
            draggingRef.current = { photoId: ids[0], entity };
            viewer.scene.screenSpaceCameraController.enableInputs = false;
          }
        }
      }
    }, Cesium.ScreenSpaceEventType.LEFT_DOWN);

    // Drag move — update entity position
    handler.setInputAction((movement: Cesium.ScreenSpaceEventHandler.MotionEvent) => {
      if (!draggingRef.current) return;
      const cartesian = viewer.camera.pickEllipsoid(movement.endPosition, viewer.scene.globe.ellipsoid);
      if (cartesian) {
        draggingRef.current.entity.position = new Cesium.ConstantPositionProperty(cartesian);
        viewer.scene.requestRender();

        // Update cursor display
        const carto = Cesium.Cartographic.fromCartesian(cartesian);
        dispatch({ type: 'SET_CURSOR', position: {
          lng: Cesium.Math.toDegrees(carto.longitude),
          lat: Cesium.Math.toDegrees(carto.latitude),
        }});
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    // Drag end — commit new position
    handler.setInputAction(() => {
      if (!draggingRef.current) return;
      const { photoId, entity } = draggingRef.current;
      const position = entity.position?.getValue(Cesium.JulianDate.now());
      if (position) {
        const carto = Cesium.Cartographic.fromCartesian(position);
        const newCoords = {
          longitude: Cesium.Math.toDegrees(carto.longitude),
          latitude: Cesium.Math.toDegrees(carto.latitude),
        };

        const oldPhoto = state.photos.find(p => p.id === photoId);
        const oldCoords = oldPhoto?.coordinates;

        dispatch({ type: 'UPDATE_PHOTO', id: photoId, updates: { coordinates: newCoords, isManuallyPlaced: true } });
        dispatch({ type: 'SET_SELECTION', ids: [photoId], itemType: 'photo' });

        if (oldCoords) {
          pushAction({
            type: 'MOVE_PHOTO',
            description: `Move photo`,
            undo: () => dispatch({ type: 'UPDATE_PHOTO', id: photoId, updates: { coordinates: oldCoords } }),
            redo: () => dispatch({ type: 'UPDATE_PHOTO', id: photoId, updates: { coordinates: newCoords } }),
          });
        }
      }

      viewer.scene.screenSpaceCameraController.enableInputs = true;
      draggingRef.current = null;
    }, Cesium.ScreenSpaceEventType.LEFT_UP);

    return () => handler.destroy();
  }, [state.mode, state.photos, dispatch, pushAction]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Floor plan overlay rendering ──────────────────────────────────────

  const loadFloorPlanLayer = useCallback((fp: FloorPlanOverlay) => {
    const viewer = viewerRef.current;
    if (!viewer || viewer.isDestroyed()) return;
    if (!fp.tileCache || !fp.tileMetadata) return;
    if (floorPlanLayersRef.current.has(fp.id)) return;

    const imgW = fp.imageWidth || fp.tileMetadata.originalWidth;
    const imgH = fp.imageHeight || fp.tileMetadata.originalHeight;
    if (!imgW || !imgH) return;

    const bounds = calculateBoundsFromTransform(fp.transform, imgW, imgH);
    const rectangle = Cesium.Rectangle.fromDegrees(bounds.west, bounds.south, bounds.east, bounds.north);

    try {
      const tilingScheme = new Cesium.GeographicTilingScheme({ rectangle, numberOfLevelZeroTilesX: 1, numberOfLevelZeroTilesY: 1 });
      const tileUrl = `/api/tiles/${fp.tileCache}/{z}/{x}/{y}.${fp.tileMetadata.format}?direct=true`;
      const provider = new Cesium.UrlTemplateImageryProvider({
        url: tileUrl, tilingScheme, minimumLevel: 0,
        maximumLevel: fp.tileMetadata.levels - 1,
        tileWidth: fp.tileMetadata.tileSize, tileHeight: fp.tileMetadata.tileSize,
      });
      const layer = viewer.imageryLayers.addImageryProvider(provider);
      layer.alpha = fp.opacity;
      layer.show = fp.visible;
      floorPlanLayersRef.current.set(fp.id, layer);
    } catch (err) {
      console.error('[MapEditor] Failed to load floor plan layer:', err);
    }
  }, []);

  // Sync floor plan layers with state
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || viewer.isDestroyed()) return;

    // Load new overlays
    state.floorPlans.forEach(fp => {
      if (!floorPlanLayersRef.current.has(fp.id) && fp.tileCache) loadFloorPlanLayer(fp);

      // Sync visibility + opacity for existing layers
      const layer = floorPlanLayersRef.current.get(fp.id);
      if (layer) {
        layer.show = fp.visible;
        layer.alpha = fp.opacity;
      }
    });

    // Remove deleted overlay layers
    floorPlanLayersRef.current.forEach((layer, id) => {
      if (!state.floorPlans.some(fp => fp.id === id)) {
        viewer.imageryLayers.remove(layer, true);
        floorPlanLayersRef.current.delete(id);
      }
    });

    viewer.scene.requestRender();
  }, [state.floorPlans, loadFloorPlanLayer]);

  // Handle "place-overlay" mode — open the AddFloorPlanModal
  useEffect(() => {
    if (state.mode === 'place-overlay') setShowAddFloorPlan(true);
  }, [state.mode]);

  const handleAddFloorPlan = useCallback((overlayData: Omit<FloorPlanOverlay, 'id' | 'createdAt' | 'updatedAt'>) => {
    const now = new Date().toISOString();
    const newOverlay: FloorPlanOverlay = {
      ...overlayData,
      id: crypto.randomUUID(),
      createdAt: now,
      updatedAt: now,
    };
    dispatch({ type: 'ADD_FLOOR_PLAN', floorPlan: newOverlay });
    dispatch({ type: 'SET_SELECTION', ids: [newOverlay.id], itemType: 'overlay' });
    dispatch({ type: 'SET_ALIGNING_OVERLAY', id: newOverlay.id });
    setShowAddFloorPlan(false);
    dispatch({ type: 'SET_MODE', mode: 'select' });

    pushAction({
      type: 'ADD_OVERLAY',
      description: `Add floor plan: ${newOverlay.name}`,
      undo: () => dispatch({ type: 'DELETE_FLOOR_PLAN', id: newOverlay.id }),
      redo: () => dispatch({ type: 'ADD_FLOOR_PLAN', floorPlan: newOverlay }),
    });
  }, [dispatch, pushAction]);

  const handleAlignConfirm = useCallback((transform: FloorPlanTransform) => {
    const overlayId = state.aligningOverlayId;
    if (!overlayId) return;

    const oldOverlay = state.floorPlans.find(fp => fp.id === overlayId);
    const oldTransform = oldOverlay?.transform;

    // Remove old layer so it reloads with new bounds
    const oldLayer = floorPlanLayersRef.current.get(overlayId);
    if (oldLayer && viewerRef.current) {
      viewerRef.current.imageryLayers.remove(oldLayer, true);
      floorPlanLayersRef.current.delete(overlayId);
    }

    dispatch({ type: 'UPDATE_FLOOR_PLAN', id: overlayId, updates: { transform, updatedAt: new Date().toISOString() } });
    dispatch({ type: 'SET_ALIGNING_OVERLAY', id: null });

    if (oldTransform) {
      pushAction({
        type: 'ALIGN_OVERLAY',
        description: 'Align floor plan',
        undo: () => dispatch({ type: 'UPDATE_FLOOR_PLAN', id: overlayId, updates: { transform: oldTransform } }),
        redo: () => dispatch({ type: 'UPDATE_FLOOR_PLAN', id: overlayId, updates: { transform } }),
      });
    }
  }, [state.aligningOverlayId, state.floorPlans, dispatch, pushAction]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement) return;

      if (e.key === 'v' || e.key === 'Escape') dispatch({ type: 'SET_MODE', mode: 'select' });
      else if (e.key === 'm') dispatch({ type: 'SET_MODE', mode: 'move' });
      else if (e.key === 'p') dispatch({ type: 'SET_MODE', mode: 'place-photo' });
      else if (e.key === 'o') dispatch({ type: 'SET_MODE', mode: 'place-overlay' });
      else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (state.selectedIds.length > 0 && state.selectedType === 'photo') {
          dispatch({ type: 'DELETE_PHOTOS', ids: state.selectedIds });
        } else if (state.selectedIds.length === 1 && state.selectedType === 'overlay') {
          dispatch({ type: 'DELETE_FLOOR_PLAN', id: state.selectedIds[0] });
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [dispatch, state.selectedIds, state.selectedType]);

  // Mode banner
  const modeBanner = state.mode === 'place-photo'
    ? 'Click on the map to place a photo — Esc to cancel'
    : state.mode === 'place-overlay'
    ? 'Upload a floor plan to add an overlay — Esc to cancel'
    : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#0d0d0d' }}>
      <EditorToolbar />

      <div style={{ display: 'flex', flex: 1, minHeight: 0, position: 'relative' }}>
        <LeftPanel
          hasOrtho={!!(project.geotiff?.orthoTileCache && project.geotiff?.orthoTileMetadata?.boundsWgs84)}
          hasDsm={!!(project.geotiff?.dsmTileCache && project.geotiff?.dsmTileMetadata?.boundsWgs84)}
          projectId={project.id}
          onPhotosImported={async () => {
            // Reload project to get updated photos
            const updated = await api.project.get(project.id);
            if (updated?.geotiff?.photos) {
              dispatch({ type: 'SET_PHOTOS', photos: updated.geotiff.photos });
            }
          }}
        />

        {/* Map canvas */}
        <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
          {modeBanner && (
            <div style={{
              position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
              zIndex: 10, background: 'rgba(79,124,255,0.9)', color: '#fff',
              padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 500,
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            }}>
              {modeBanner}
            </div>
          )}
          <div ref={containerRef} style={{ width: '100%', height: '100%', cursor: state.mode === 'place-photo' ? 'crosshair' : state.mode === 'move' ? 'move' : 'default' }} />
        </div>

        <RightPropertiesPanel />
      </div>

      <StatusBar />

      {/* Add Floor Plan Modal */}
      <AddFloorPlanModal
        isOpen={showAddFloorPlan}
        onClose={() => { setShowAddFloorPlan(false); dispatch({ type: 'SET_MODE', mode: 'select' }); }}
        onAdd={handleAddFloorPlan}
        defaultCenter={state.cursorPosition || undefined}
      />

      {/* Floor Plan Aligner */}
      {state.aligningOverlayId && viewerRef.current && (() => {
        const fp = state.floorPlans.find(f => f.id === state.aligningOverlayId);
        if (!fp || !fp.tileCache) return null;
        const imgW = fp.imageWidth || fp.tileMetadata?.originalWidth || 1000;
        const imgH = fp.imageHeight || fp.tileMetadata?.originalHeight || 1000;
        const tileUrl = `/api/tiles/${fp.tileCache}/0/0/0.${fp.tileMetadata?.format || 'png'}?direct=true`;
        return (
          <FloorPlanAligner
            viewer={viewerRef.current!}
            imageUrl={tileUrl}
            imageWidth={imgW}
            imageHeight={imgH}
            initialTransform={fp.transform}
            onConfirm={handleAlignConfirm}
            onCancel={() => dispatch({ type: 'SET_ALIGNING_OVERLAY', id: null })}
          />
        );
      })()}
    </div>
  );
}

// ─── Page wrapper ──────────────────────────────────────────────────────────

export function MapEditorPage() {
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    api.project.get(id)
      .then(setProject)
      .catch((err: any) => setError(err.message || 'Failed to load project'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0d0d0d', color: '#94a3b8' }}>Loading project...</div>;
  }

  if (error || !project) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0d0d0d', color: '#fca5a5' }}>{error || 'Project not found'}</div>;
  }

  return (
    <DesktopGate>
      <MapEditorProvider>
        <MapEditorContent project={project} />
      </MapEditorProvider>
    </DesktopGate>
  );
}
